import { gtag } from "@/index";
import { fetchServer } from "@features/utils";
import posthog from "posthog-js";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { UserType } from "./type";
import {
  clearOnboardingPreferences,
  getOnboardingPreferences,
  hasOnboardingPreferences,
} from "@views/welcome/upload-intro";

// If ?login-to-app in url, send jwt to app
const logInAppIfNeeded = () => {
  if (document.location.search.includes("login-to-app")) {
    const token = localStorage.getItem("token");
    if (token) {
      setTimeout(() => {
        window.location.href = "photographeai://login?token=" + token;
      }, 50);
    }
  }
};
logInAppIfNeeded();

// Check if coming from upload flow
const isFromUploadFlow = () => {
  return new URLSearchParams(window.location.search).get("upload") === "1";
};

// Get UTM parameters from URL
const getUtmParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // Cookie utms=a,b,c
  const cookiesUtms =
    document.cookie
      .split("; ")
      .find((row) => row.startsWith("utms="))
      ?.split("=")?.[1]
      ?.split(",") || [];
  return {
    utm_source: [urlParams.get("utm_source") || "", ...cookiesUtms]
      .filter(Boolean)
      .join(","),
  };
};

// If ?token in url, set it to localstorage
if (document.location.search.includes("token=")) {
  const token = document.location.search.split("token=")[1];
  localStorage.setItem("token", token);
}

export const useUser = () => {
  const client = useQueryClient();

  const user = useQuery("user", async () => {
    try {
      return await fetchServer<UserType>("/api/auth/me");
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    if (
      user.data &&
      !localStorage.getItem("registered_sign_up") &&
      user.data.created_at > Date.now() - 1000 * 60 * 10 // Check if user signed up in the last 10 minutes
    ) {
      localStorage.setItem("registered_sign_up", "true");
      gtag({
        event: "sign_up",
        utm_source: getUtmParams().utm_source || "unknown",
      });
    }

    if (user.data?.id) {
      posthog.identify(user.data.id, {
        name: user.data.name,
      });

      // Send onboarding preferences if they exist and haven't been sent yet
      // This handles the case where user answers questions before signing up
      const sendOnboardingPreferencesIfNeeded = async () => {
        try {
          if (hasOnboardingPreferences()) {
            const prefs = getOnboardingPreferences();
            // Send to backend
            await fetchServer("/api/auth/me", {
              analytics: {
                features: prefs.features,
                sources: prefs.sources,
              },
            });
            // Clear after successful send
            clearOnboardingPreferences();
            posthog.capture("onboarding_preferences_sent_on_login", {
              features: prefs.features,
              sources: prefs.sources,
            });
          }
        } catch (e) {
          console.error("Failed to send onboarding preferences:", e);
        }
      };

      sendOnboardingPreferencesIfNeeded();
    }
  }, [user.data]);

  const login = async (type = "google", token: string, optionalArgs?: any) => {
    try {
      // Get UTM parameters to pass to the backend
      const utmParams = getUtmParams();
      const onboardingPath = window.location.pathname.includes("/welcome")
        ? "free_trial"
        : "direct";

      const data = await fetchServer<{ token: string }>("/api/auth/" + type, {
        token,
        options: {
          ...optionalArgs,
          utm_source: utmParams.utm_source,
          onboarding_path: onboardingPath,
        },
      });
      if (data.token) {
        if ((window as any).ReactNativeWebView) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({ type: "login", token: data.token }),
          );
        }

        gtag({
          event: "log_in",
          method: type,
          utm_source: getUtmParams().utm_source || "unknown",
        });

        localStorage.setItem("token", data.token);
        logInAppIfNeeded();

        const topDomain = window.location.hostname
          .split(".")
          .slice(-2)
          .join(".");
        document.cookie =
          "hasAccount=true; domain=." +
          topDomain +
          "; path=/; max-age=31536000; SameSite=Lax; Secure";

        // If ?return_to= in url, redirect there
        const urlParams = new URLSearchParams(window.location.search);
        const returnTo = urlParams.get("return_to");
        if (returnTo) {
          window.location.href = returnTo;
          return;
        }

        // If coming from upload flow, redirect to /upload?complete=1
        if (isFromUploadFlow()) {
          window.location.href = "/upload?complete=1";
          return;
        }

        client.invalidateQueries("user");
      } else {
        throw new Error("Failed to login");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to login");
    }
  };

  const logout = async () => {
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({ type: "logout" }),
      );
    }
    // Clear all local storage except the ones starting with "ph_" or "persist_"
    Object.keys(localStorage).forEach((key) => {
      if (!key.startsWith("ph_") && !key.startsWith("persist_")) {
        localStorage.removeItem(key);
      }
    });
    posthog.reset();
    client.invalidateQueries("user");
    document.location.href = "/";
  };

  const consent = async (
    type: "models" | "styles" | "images" | "initial",
    value?: string,
  ) => {
    try {
      await fetchServer(
        "/api/auth/me",
        type === "initial"
          ? {
              consent: true,
            }
          : { [type]: value },
      );
      client.invalidateQueries("user");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update consent");
    }
  };

  const setAnalytics = async (analytics: {
    features: string[];
    sources: string[];
  }) => {
    try {
      await fetchServer("/api/auth/me", {
        analytics,
      });
    } catch (e) {
      console.error(e);
      toast.error("Failed to update consent");
    }
  };

  const cancelSubscription = async () => {
    try {
      await fetchServer("/api/billing/cancel", {});
      client.invalidateQueries("user");
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Failed to cancel subscription");
      return false;
    }
  };

  const claimFreeCredits = async (
    action: string,
    data?: { rating?: number; text?: string; type?: string },
  ) => {
    try {
      const response = await fetchServer<{ credits: number }>(
        `/api/auth/me/freecredits/${action}`,
        data || {},
      );
      client.invalidateQueries("user");
      return response;
    } catch (e) {
      console.error(e);
      toast.error("Failed to claim free credits");
      return null;
    }
  };

  const submitSuggestion = async (text: string) => {
    try {
      await fetchServer("/api/auth/me/suggestion", {
        text: text.slice(0, 2048),
      });
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Failed to submit suggestion");
      return false;
    }
  };

  const deleteAccount = async () => {
    try {
      await fetchServer("/api/auth/me/delete", {});
      toast.success("Account deletion scheduled. You have 48 hours to cancel.");
      client.invalidateQueries("user");
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Failed to schedule account deletion");
      return false;
    }
  };

  const cancelAccountDeletion = async () => {
    try {
      await fetchServer("/api/auth/me/cancel-delete", {});
      toast.success("Account deletion has been cancelled");
      client.invalidateQueries("user");
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Failed to cancel account deletion");
      return false;
    }
  };

  return {
    user,
    credits: (user.data?.credits || 0) + (user.data?.additional_credits || 0),
    login,
    logout,
    refresh: () => client.invalidateQueries("user"),
    consent,
    setAnalytics,
    cancelSubscription,
    claimFreeCredits,
    submitSuggestion,
    deleteAccount,
    cancelAccountDeletion,
  };
};

export const useUserRefreshFromApp = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Event received from the native app
    window.addEventListener("app-message", (event: any) => {
      const { type, payload } = event.detail;
      if (type === "token-received") {
        console.log("Received token from app:", payload.token);
        localStorage.setItem("token", payload.token);
        queryClient.invalidateQueries("user");
      }
    });
  }, [queryClient]);
};

export const useHideCosts = () => {
  const { credits, user } = useUser();
  return (
    credits <= 150 &&
    credits > 50 &&
    user?.data?.plan === "free" &&
    Date.now() - user.data.created_at < 1000 * 60 * 60 * 24 * 7
  );
};
